import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useAppStore } from '../store/appStore';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { useTheme } from '../hooks/useTheme';
import edgelexLogoStatic from '../assets/edgelex-logo-static.png';
import {
  ChatBubbleLeftRightIcon,
  UserPlusIcon,
  UsersIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  BeakerIcon,
  ClipboardDocumentListIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  CheckIcon,
  AcademicCapIcon,
  FolderIcon,
  CpuChipIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

interface SidebarProps {
  className?: string;
}

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: string[];
}

interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
  roles?: string[];
}

const Sidebar: React.FC<SidebarProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { keycloak } = useKeycloak();
  const userPermissions = useUserPermissions();
  const { isSidebarExpanded, toggleSidebar } = useAppStore();
  const { theme, setTheme } = useTheme();



  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(['ai-workflow', 'case-management'])
  );
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);

  // Navigation data with Heroicons
  const navigationGroups: NavGroup[] = [
    {
      id: 'ai-workflow',
      label: 'AI Workflow',
      items: [
        { id: 'ai-chat', label: 'AI Chat', path: '/chat', icon: ChatBubbleLeftRightIcon },
        { id: 'projects', label: 'Projects', path: '/projects', icon: FolderIcon },
        { id: 'agents', label: 'Agents', path: '/agents', icon: CpuChipIcon },
        { id: 'client-intake', label: 'Client Intake', path: '/intake', icon: UserPlusIcon },
      ],
    },
    {
      id: 'case-management',
      label: 'Engagement Management',
      items: [
        { id: 'tasks', label: 'Tasks', path: '/tasks', icon: ClipboardDocumentListIcon },
        { id: 'contacts', label: 'Contacts', path: '/contacts', icon: UsersIcon },
        { id: 'matters', label: 'Engagements', path: '/engagements', icon: BriefcaseIcon },
        { id: 'documents', label: 'Documents', path: '/documents', icon: DocumentTextIcon },
        { id: 'email', label: 'Email', path: '/email', icon: EnvelopeIcon },
        { id: 'calendar', label: 'Calendar', path: '/calendar', icon: CalendarDaysIcon, roles: ['attorney', 'admin', 'assistant'] },
      ],
    },
    {
      id: 'business-tools',
      label: 'Business Tools',
      items: [
        { id: 'billing', label: 'Billing', path: '/billing', icon: CreditCardIcon },
      ],
    },
    {
      id: 'admin-tools',
      label: 'Admin Tools',
      roles: ['admin', 'system-admin'],
      items: [
        { id: 'user-management', label: 'User Management', path: '/admin/user-management', icon: UsersIcon, roles: ['admin'] },
        { id: 'firm-settings', label: 'Subscriber Settings', path: '/admin/settings', icon: Cog6ToothIcon, roles: ['admin'] },
        { id: 'edgemail-admin', label: 'WeBeMail', path: '/admin/edgemail', icon: EnvelopeIcon, roles: ['admin'] },
      ],
    },
    {
      id: 'system-tools',
      label: 'System Tools',
      roles: ['admin', 'system-admin', 'david-duncan'],
      items: [
        { id: 'system-monitor', label: 'System Monitor', path: '/system-monitor', icon: ChartBarIcon, roles: ['admin', 'system-admin'] },
        { id: 'communications', label: 'Communications', path: '/system-tools/communications', icon: ChatBubbleLeftRightIcon, roles: ['admin', 'system-admin'] },
        { id: 'logs', label: 'Logs', path: '/logs', icon: EyeIcon, roles: ['admin', 'system-admin'] },
        { id: 'chat-test-runner', label: 'Chat Test Runner', path: '/chat_test_runner', icon: BeakerIcon, roles: ['admin', 'system-admin'] },
        { id: 'sudo-dashboard', label: 'SUDO Dashboard', path: '/system-monitor/sudo', icon: ShieldCheckIcon },
        { id: 'few-shot-review', label: 'AI Training', path: '/system-monitor/few-shot-review', icon: AcademicCapIcon, roles: ['admin', 'system-admin'] },
      ],
    },
  ];

  // Theme options with proper icons
  const themeOptions = [
    { id: 'light', label: 'Light Mode', icon: SunIcon },
    { id: 'dark', label: 'Dark Mode', icon: MoonIcon },
    { id: 'system', label: 'System', icon: ComputerDesktopIcon }
  ];

  // Permission checking
  const hasPermission = useCallback((requiredRoles?: string[]) => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    if (userPermissions.isLoading) return false;

    return requiredRoles.some(role => userPermissions.roles.includes(role));
  }, [userPermissions]);

  // Filter navigation based on permissions
  const filteredGroups = useMemo(() => {
    const filtered = navigationGroups
      .filter(group => hasPermission(group.roles))
      .map(group => ({
        ...group,
        items: group.items.filter(item => hasPermission(item.roles))
      }))
      .filter(group => group.items.length > 0);

    return filtered;
  }, [hasPermission, userPermissions]);

  // Check if route is active
  const isActive = useCallback((path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  // Check if group has active item
  const hasActiveItem = useCallback((group: NavGroup) => {
    return group.items.some(item => isActive(item.path));
  }, [isActive]);

  // Handle navigation
  const handleNavigate = useCallback((path: string) => {
    console.log('🚀 SIDEBAR NAVIGATION:', {
      clickedPath: path,
      currentLocation: location.pathname,
      authenticated: keycloak.authenticated,
      routerReady: true
    });
    navigate(path);
    console.log('🚀 Navigate function called for:', path);
  }, [navigate, location.pathname, keycloak.authenticated]);

  // Handle logout
  const handleLogout = useCallback(async () => {
    try {
      // Clear any local storage
      localStorage.clear();
      sessionStorage.clear();

      // Logout from Keycloak
      if (keycloak.authenticated) {
        await keycloak.logout({
          redirectUri: window.location.origin + '/login'
        });
      } else {
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Logout failed:', error);
      // Force redirect to login if logout fails
      window.location.href = '/login';
    }
  }, [keycloak]);

  // Toggle group expansion
  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  }, []);

  // Handle theme change
  const handleThemeChange = useCallback((newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    setIsThemeMenuOpen(false);
  }, [setTheme]);

  // Get user info for display
  const getUserInfo = useCallback(() => {
    if (!keycloak.tokenParsed) return null;

    const token = keycloak.tokenParsed;
    const name = token.name || token.preferred_username || token.given_name || token.family_name || 'User';
    const email = token.email || token.upn || token.preferred_username || 'No email available';
    const initials = name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

    return { name, email, initials };
  }, [keycloak.tokenParsed]);

  const userInfo = getUserInfo();

  if (userPermissions.isLoading) {
    return (
      <div className="w-16 h-full bg-[var(--brand-surface-sidebar)] flex items-center justify-center border-r border-[var(--brand-border)]">
        <div className="w-5 h-5 border-2 border-[var(--brand-accent-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className={`
        bg-[var(--brand-surface-sidebar)]
        border-r border-[var(--brand-border)]
        flex flex-col h-full
        transition-all duration-300 ease-out
        ${isSidebarExpanded ? 'w-64' : 'w-16'}
        ${className}
      `}
    >

      {/* Header */}
      <div className="flex items-center p-4 border-b border-[var(--brand-border)]">
        <button
          onClick={toggleSidebar}
          className="flex items-center justify-center w-8 h-8 text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)] hover:bg-[var(--brand-surface-hover)] rounded-lg transition-all duration-200 flex-shrink-0"
          title={isSidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          <Bars3Icon className="w-5 h-5" />
        </button>

        {isSidebarExpanded && (
          <div className="ml-3 flex items-center space-x-2 animate-in slide-in-from-left-2 duration-300">
            <img
              src={edgelexLogoStatic}
              alt="WeBelieve logo"
              className="w-7 h-7 rounded-full flex-shrink-0"
            />
            <div>
              <h1 className="text-[var(--brand-text-primary)] font-bold text-lg leading-tight">WeBelieve</h1>
              <p className="text-[var(--brand-text-tertiary)] text-xs leading-tight">Business AI Platform</p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className={`flex-1 p-3 space-y-1 ${
        isSidebarExpanded
          ? 'overflow-y-auto scrollbar-thin scrollbar-thumb-[var(--brand-border-strong)] scrollbar-track-transparent hover:scrollbar-thumb-[var(--brand-text-tertiary)]'
          : 'overflow-y-hidden'
      }`}>
        {filteredGroups.map((group) => {
          const isGroupExpanded = expandedGroups.has(group.id);
          const isGroupActive = hasActiveItem(group);

          return (
            <div key={group.id} className="space-y-1">

              {/* Group Header */}
              {isSidebarExpanded && (
                <button
                  onClick={() => toggleGroup(group.id)}
                  className={`
                    flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-lg
                    transition-all duration-200 group
                    ${isGroupActive
                      ? 'text-[var(--brand-accent-deep)] bg-[var(--brand-accent-primary-soft)] shadow-sm'
                      : 'text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-secondary)] hover:bg-[var(--brand-surface-hover)]'
                    }
                  `}
                >
                  <span className="font-semibold">{group.label}</span>
                  <ChevronDownIcon
                    className={`w-4 h-4 transition-transform duration-200 ${
                      isGroupExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </button>
              )}

              {/* Group Items */}
              <div className={`space-y-1 ${
                isSidebarExpanded ? (isGroupExpanded ? 'block' : 'hidden') : 'block'
              }`}>
                {group.items.map((item) => {
                  const itemIsActive = isActive(item.path);
                  const IconComponent = item.icon;

                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNavigate(item.path)}
                      className={`
                        flex items-center w-full px-3 py-2.5 rounded-lg transition-all duration-200 group
                        ${isSidebarExpanded ? 'justify-start space-x-3' : 'justify-center'}
                        ${itemIsActive
                          ? 'bg-[var(--brand-accent-primary)] text-[var(--brand-accent-contrast)] shadow-lg'
                          : 'text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)] hover:bg-[var(--brand-surface-hover)]'
                        }
                      `}
                      title={!isSidebarExpanded ? item.label : undefined}
                    >
                      <IconComponent className="w-5 h-5 flex-shrink-0" />
                      {isSidebarExpanded && (
                        <span className="text-sm font-medium truncate">{item.label}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* User Menu & Theme Switcher */}
      <div className="border-t border-[var(--brand-border)] p-3 space-y-2">

        {/* Theme Switcher */}
        <div className="relative">
          <button
            onClick={() => setIsThemeMenuOpen(!isThemeMenuOpen)}
            className={`
              flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200
              ${isSidebarExpanded ? 'justify-between' : 'justify-center'}
              text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)] hover:bg-[var(--brand-surface-hover)]
            `}
            title={!isSidebarExpanded ? 'Change theme' : undefined}
          >
            <div className={`flex items-center ${isSidebarExpanded ? 'space-x-3' : ''}`}>
              {theme === 'light' && <SunIcon className="w-5 h-5" />}
              {theme === 'dark' && <MoonIcon className="w-5 h-5" />}
              {theme === 'system' && <ComputerDesktopIcon className="w-5 h-5" />}
              {isSidebarExpanded && (
                <span className="text-sm font-medium">Theme</span>
              )}
            </div>
            {isSidebarExpanded && (
              <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${
                isThemeMenuOpen ? 'rotate-180' : ''
              }`} />
            )}
          </button>

          {/* Theme Dropdown */}
          {isThemeMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsThemeMenuOpen(false)}
              />
              <div className={`
                absolute bottom-full mb-1
                bg-[var(--brand-surface-card)]
                border border-[var(--brand-border)]
                rounded-lg shadow-xl backdrop-blur-sm z-50
                animate-in fade-in-0 zoom-in-95 duration-200
                ${isSidebarExpanded ? 'left-0 right-0' : 'left-0 w-48'}
              `}>
                {themeOptions.map((option) => {
                  const IconComponent = option.icon;
                  const isSelected = theme === option.id;

                  return (
                    <button
                      key={option.id}
                      onClick={() => handleThemeChange(option.id as 'light' | 'dark' | 'system')}
                      className="flex items-center justify-between w-full px-3 py-2 text-sm text-[var(--brand-text-secondary)] hover:bg-[var(--brand-surface-hover)] first:rounded-t-lg last:rounded-b-lg transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <IconComponent className="w-4 h-4" />
                        <span>{option.label}</span>
                      </div>
                      {isSelected && (
                        <CheckIcon className="w-4 h-4 text-[var(--brand-accent-primary)]" />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* User Menu */}
        {userInfo && (
          <div className="relative">
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className={`
                flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 group
                ${isSidebarExpanded ? 'space-x-3' : 'justify-center'}
                text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)] hover:bg-[var(--brand-surface-hover)]
              `}
              title={!isSidebarExpanded ? `${userInfo.name} (${userInfo.email})` : undefined}
            >
              <div className="w-8 h-8 bg-[var(--brand-accent-primary)] rounded-full flex items-center justify-center text-[var(--brand-accent-contrast)] text-sm font-semibold flex-shrink-0">
                {userInfo.initials}
              </div>
              {isSidebarExpanded && (
                <>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-[var(--brand-text-primary)] truncate">{userInfo.name}</p>
                    <p className="text-xs text-[var(--brand-text-tertiary)] truncate">{userInfo.email}</p>
                  </div>
                  <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${
                    isUserMenuOpen ? 'rotate-180' : ''
                  }`} />
                </>
              )}
            </button>

            {/* User Dropdown */}
            {isUserMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsUserMenuOpen(false)}
                />
                <div className={`
                  absolute bottom-full mb-1
                  bg-[var(--brand-surface-card)]
                  border border-[var(--brand-border)]
                  rounded-lg shadow-xl backdrop-blur-sm z-50
                  animate-in fade-in-0 zoom-in-95 duration-200
                  ${isSidebarExpanded ? 'left-0 right-0' : 'left-0 w-48'}
                `}>
                  <div className="p-2">
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-3 w-full px-3 py-2 text-sm text-[var(--brand-status-danger)] hover:bg-[color-mix(in oklab, var(--brand-status-danger) 18%, transparent)] rounded-lg transition-colors"
                    >
                      <ArrowRightOnRectangleIcon className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
